import React, { useState, useEffect } from 'react';

interface Industry {
  id: number;
  name: string;
}

interface IndustriesFilterModalProps {
  show: boolean;
  selectedIndustryIds: number[];
  onClose: () => void;
  onApply: (industryIds: number[]) => void;
}

const IndustriesFilterModal: React.FC<IndustriesFilterModalProps> = ({
  show,
  selectedIndustryIds,
  onClose,
  onApply
}) => {
  const [allIndustries, setAllIndustries] = useState<Industry[]>([]);
  const [tempSelected, setTempSelected] = useState<number[]>(selectedIndustryIds);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setTempSelected(selectedIndustryIds);
      setSearchTerm('');
      // Fetch industries only when the modal is shown and data is not yet loaded
      if (allIndustries.length === 0) {
        setLoading(true);
        fetch('/api/v1/industries')
          .then(response => response.json())
          .then(data => {
            setAllIndustries(data);
            setLoading(false);
          })
          .catch(error => {
            console.error('Failed to fetch industries:', error);
            setLoading(false);
          });
      }
    }
  }, [show, selectedIndustryIds]);

  const handleToggle = (industryId: number) => {
    const newSelected = tempSelected.includes(industryId)
      ? tempSelected.filter(id => id !== industryId)
      : [...tempSelected, industryId];
    
    setTempSelected(newSelected);
  };

  const handleSelectAll = () => {
    setTempSelected(filteredIndustries.map(industry => industry.id));
  };

  const handleClearAll = () => {
    setTempSelected([]);
  };

  const handleApply = () => {
    onApply(tempSelected);
    onClose();
  };

  const filteredIndustries = allIndustries.filter(industry => 
    industry.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!show) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="modal-backdrop fade show" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 10060 
        }}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="modal fade show d-block" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 10061,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        tabIndex={-1}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div className="modal-dialog modal-dialog-scrollable modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">業界を選択</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="閉じる"
              />
            </div>
            
            <div className="modal-body">
              {/* 選択/解除ボタン */}
              <div className="d-flex gap-2 mb-3">
                <button 
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleSelectAll}
                >
                  一括選択
                </button>
                <button 
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleClearAll}
                >
                  一括解除
                </button>
                <div className="ms-auto text-muted small">
                  {tempSelected.length}件選択中
                </div>
              </div>

              {/* 検索フィールド */}
              <div className="mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="業界を検索..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">読み込み中...</span>
                  </div>
                </div>
              ) : (
                <div className="row g-2">
                  {filteredIndustries.map(industry => (
                    <div key={industry.id} className="col-6 col-md-4 col-lg-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`industry-${industry.id}`}
                          checked={tempSelected.includes(industry.id)}
                          onChange={() => handleToggle(industry.id)}
                        />
                        <label className="form-check-label" htmlFor={`industry-${industry.id}`} style={{ cursor: 'pointer' }}>
                          {industry.name}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={onClose}
              >
                キャンセル
              </button>
              <button 
                type="button" 
                className="btn btn-primary"
                onClick={handleApply}
              >
                この条件で検索
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default IndustriesFilterModal;
